import {
    Box, InputAdornment, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TextField, Tooltip, Typography, Autocomplete,
    IconButton
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SearchIcon from '@mui/icons-material/Search'
import CasinoIcon from '@mui/icons-material/Casino'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import TrendingDownIcon from '@mui/icons-material/TrendingDown'
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered'
import { useLocation, useNavigate } from 'react-router-dom'
import { ApiGetAdminAllSpins } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'
import { useDateRange } from '../../hooks/useDateRange'
import DateRangeFilter from '../../Components/Common/DateRangeFilter'
import { usePersistedState } from '../../hooks/userPersistedState'

interface SpinEntry {
    _id: string
    type: string
    direction: 'CREDIT' | 'DEBIT'
    spinCount: number
    referenceType?: string
    referenceId?: string
    metadata?: Record<string, any>
    createdAt: string
    seen?: boolean
    userId: string
    userName: string
    userEmail: string
}

interface SummaryRow {
    _id: { type: string; direction: 'CREDIT' | 'DEBIT' }
    totalSpins: number
    count: number
}

interface Meta {
    pagination: {
        page: number
        limit: number
        total: number
        totalPages: number
        hasNext: boolean
        hasPrev: boolean
    }
}

const formatDate = (date: string) =>
    new Date(date)?.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

const formatTime = (date: string) =>
    new Date(date)?.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })

const directionMap: Record<string, { color: string; bg: string; border: string; sign: string }> = {
    CREDIT: { color: '#10b981', bg: '#10b98118', border: '#10b98150', sign: '+' },
    DEBIT: { color: '#ef4444', bg: '#ef444418', border: '#ef444450', sign: '−' },
}

const DirectionPill: React.FC<{ direction: string }> = ({ direction }) => {
    const d = directionMap[direction] || { color: '#9a9a9a', bg: '#9a9a9a18', border: '#9a9a9a50', sign: '' }
    return (
        <Box sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.6,
            px: 1.5, py: 0.4, borderRadius: '6px', fontSize: 11, fontWeight: 700,
            color: d.color, backgroundColor: d.bg, border: `1px solid ${d.border}`,
            letterSpacing: '0.03em', whiteSpace: 'nowrap',
        }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', background: d.color, flexShrink: 0 }} />
            {direction}
        </Box>
    )
}

const TypePill: React.FC<{ type: string }> = ({ type }) => (
    <Box sx={{
        display: 'inline-flex', alignItems: 'center',
        px: 1.3, py: 0.35, borderRadius: '6px', fontSize: 10.5, fontWeight: 700,
        color: '#a78bfa', backgroundColor: '#a78bfa14', border: '1px solid #a78bfa40',
        letterSpacing: '0.04em', whiteSpace: 'nowrap',
    }}>
        {type}
    </Box>
)

const StatCard: React.FC<{ label: string; value: string; sub?: string; color: string; icon: React.ReactNode }> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333',
        borderRadius: '14px',
        p: 2.5,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.25s',
        '&:hover': { borderColor: color + '60' },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">{label}</Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>{value}</Typography>
        {sub && <Typography fontSize={10} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)

const DIRECTION_OPTIONS = ['CREDIT', 'DEBIT']

const SpinLogs: React.FC = () => {
    const {
        range, customStart, setCustomStart, customEnd, setCustomEnd, handleRangeChange, buildQuery,
    } = useDateRange('spin-logs', 'all')

    const location = useLocation()
    const navigate = useNavigate()
    const routeUserId = (location.state as any)?.userId as string | undefined
    const routeUserName = (location.state as any)?.userName as string | undefined

    const [entries, setEntries] = useState<SpinEntry[]>([])
    const [meta, setMeta] = useState<Meta>({ pagination: { page: 1, limit: 25, total: 0, totalPages: 1, hasNext: false, hasPrev: false } })
    const [summary, setSummary] = useState<SummaryRow[]>([])

    const [search, setSearch] = usePersistedState('spinLogs:search', '')
    const [page, setPage] = usePersistedState('spinLogs:page', 0)
    const [rowsPerPage, setRowsPerPage] = usePersistedState('spinLogs:rowsPerPage', 25)
    const [type, setType] = usePersistedState('spinLogs:type', '')
    const [direction, setDirection] = usePersistedState('spinLogs:direction', '')
    const [typeOptions, setTypeOptions] = useState<string[]>([])
    const [debouncedSearch, setDebouncedSearch] = useState(search)

    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({ open: false, message: '' })

    const fetchSpins = useCallback(async () => {
        try {
            const dateQuery = buildQuery()
            if (dateQuery === null) return

            const params = new URLSearchParams()
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))
            if (debouncedSearch) params.append('search', debouncedSearch)
            if (type) params.append('type', type)
            if (direction) params.append('direction', direction)
            if (routeUserId) params.append('userId', routeUserId)
            if (dateQuery) {
                new URLSearchParams(dateQuery).forEach((value, key) => params.append(key, value))
            }

            const res = await ApiGetAdminAllSpins(`?${params.toString()}`)
            if (res?.status) {
                const list: SpinEntry[] = res.data?.entries || []
                const rawSummary: SummaryRow[] = res.data?.summary || []
                // the aggregation can return a stray bucket with no type/direction
                // (docs missing those fields) — drop it, it's not a real category
                const cleanSummary = rawSummary.filter(s => s._id?.type && s._id?.direction)
                setEntries(list)
                setSummary(cleanSummary)
                if (res.meta?.pagination) setMeta({ pagination: res.meta.pagination })
                setTypeOptions(prev => {
                    const found = cleanSummary.map(s => s._id.type)
                    return Array.from(new Set([...prev, ...found]))
                })
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || error || 'Failed to fetch spin logs',
            })
        }
    }, [page, rowsPerPage, debouncedSearch, type, direction, routeUserId, buildQuery])

    useEffect(() => {
        fetchSpins()
    }, [fetchSpins])

    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), 500)
        return () => clearTimeout(timer)
    }, [search])

    const prevFiltersRef = React.useRef({ debouncedSearch, type, direction, range, customStart, customEnd })
    useEffect(() => {
        const prev = prevFiltersRef.current
        const changed =
            prev.debouncedSearch !== debouncedSearch ||
            prev.type !== type ||
            prev.direction !== direction ||
            prev.range !== range ||
            (prev.customStart?.valueOf() !== customStart?.valueOf()) ||
            (prev.customEnd?.valueOf() !== customEnd?.valueOf())
        if (changed) setPage(0)
        prevFiltersRef.current = { debouncedSearch, type, direction, range, customStart, customEnd }
    }, [debouncedSearch, type, direction, range, customStart, customEnd, setPage])

    const creditTotal = summary.filter(s => s._id.direction === 'CREDIT').reduce((sum, s) => sum + s.totalSpins, 0)
    const debitTotal = summary.filter(s => s._id.direction === 'DEBIT').reduce((sum, s) => sum + s.totalSpins, 0)
    const creditEntries = summary.filter(s => s._id.direction === 'CREDIT').reduce((sum, s) => sum + s.count, 0)
    const debitEntries = summary.filter(s => s._id.direction === 'DEBIT').reduce((sum, s) => sum + s.count, 0)

    return (
        <div className='common-layout flex-column'>
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <IconButton sx={{ ':hover': { backgroundColor: '#ffffff1A' } }} onClick={() => navigate(-1)}>
            <ArrowBackIcon sx={{ color: "#ffffff" }} />
          </IconButton>
                    <Typography className='fs-24 text-white fw-medium' sx={{ lineHeight: 1.1 }}>
                        Spin Logs{routeUserName ? ` — ${routeUserName}` : ''}
                    </Typography>
                </Box>
                {routeUserId && (
                    <Box
                        onClick={() => navigate(-1)}
                        sx={{
                            cursor: 'pointer', fontSize: 12, fontWeight: 700, color: '#ff6a3d',
                            border: '1px solid #ff6a3d40', borderRadius: '8px', px: 2, py: 0.8,
                            background: '#ff6a3d10', '&:hover': { background: '#ff6a3d20' },
                        }}
                    >
                        ← Back
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: '1fr 1fr 1fr' }, gap: 2, mb: 3 }}>
                <StatCard label="Spins Credited" value={String(creditTotal)}
                    sub={`${creditEntries} entries`}
                    color="#10b981" icon={<TrendingUpIcon fontSize="small" />} />
                <StatCard label="Spins Debited" value={String(debitTotal)}
                    sub={`${debitEntries} entries`}
                    color="#ef4444" icon={<TrendingDownIcon fontSize="small" />} />
                <StatCard label="Total Log Entries" value={String(meta.pagination.total)}
                    sub="matching current filters" color="#a78bfa"
                    icon={<FormatListNumberedIcon fontSize="small" />} />
            </Box>

            <DateRangeFilter
                range={range}
                onRangeChange={handleRangeChange}
                customStart={customStart}
                onCustomStartChange={setCustomStart}
                customEnd={customEnd}
                onCustomEndChange={setCustomEnd}
            />

            <Box className="bg-dark-color border-10 p-0 mt-2" sx={{ overflow: 'hidden' }}>
                <Box sx={{ p: '16px 20px', borderBottom: '1px solid #1e1e1e', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                    <Typography fontSize={13} fontWeight={700} color="#e0e0e0">
                        Spin Logs
                        {meta.pagination.total > 0 && (
                            <Box component="span" sx={{ ml: 1, background: 'rgba(255,255,255,0.08)', borderRadius: '10px', px: 1, py: 0.2, fontSize: 11 }}>
                                {meta.pagination.total}
                            </Box>
                        )}
                    </Typography>

                    <Box className="d-flex align-items-center" sx={{ flexWrap: 'wrap', gap: 1.5 }}>
                        <Autocomplete
                            size="small"
                            value={type}
                            disableClearable={!type}
                            onChange={(_, newValue) => setType(newValue || '')}
                            options={typeOptions}
                            sx={{ width: 190, flexShrink: 0 }}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Type" sx={filterFieldStyle}
                                    InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                />
                            )}
                            PaperComponent={({ children }) => (
                                <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                    {children}
                                </Box>
                            )}
                        />

                        <Autocomplete
                            size="small"
                            value={direction}
                            disableClearable={!direction}
                            onChange={(_, newValue) => setDirection(newValue || '')}
                            options={DIRECTION_OPTIONS}
                            sx={{ width: 150, flexShrink: 0 }}
                            renderInput={(params) => (
                                <TextField {...params} placeholder="Direction" sx={filterFieldStyle}
                                    InputProps={{ ...params.InputProps, sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } } }}
                                />
                            )}
                            PaperComponent={({ children }) => (
                                <Box className="bg-dark-color" sx={{ color: '#fff', '& .MuiAutocomplete-option': { '&:hover': { bgcolor: '#241815' }, "&[aria-selected='true']": { bgcolor: '#241815' } } }}>
                                    {children}
                                </Box>
                            )}
                        />

                        {!routeUserId && (
                            <TextField
                                placeholder="Search by User, Email"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                size="small"
                                sx={{ ...filterFieldStyle, width: 280, flexShrink: 0 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon className='color-primary' />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    </Box>
                </Box>

                <TableContainer sx={{ maxHeight: '60vh' }}>
                    <Table stickyHeader sx={{ '& .MuiTableCell-root': { borderBottom: '1px solid #1e1e1e' } }}>
                        <TableHead>
                            <TableRow>
                                {(routeUserId
                                    ? ['#', 'Type', 'Direction', 'Spins', 'Reference', 'Date & Time']
                                    : ['#', 'User', 'Type', 'Direction', 'Spins', 'Reference', 'Date & Time']
                                ).map(h => (
                                    <TableCell key={h} sx={thStyle}>{h}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {entries.length > 0 ? entries.map((entry, i) => (
                                <TableRow key={entry._id} sx={trHover}>
                                    <TableCell sx={tdStyle}>{page * rowsPerPage + i + 1}</TableCell>

                                    {!routeUserId && (
                                        <TableCell
                                            sx={{ ...tdStyle, cursor: 'pointer' }}
                                            onClick={() => navigate('/admin/user-activity', {
                                                state: { userId: entry.userId, userName: entry.userName }
                                            })}
                                        >
                                            <Typography fontSize={13} fontWeight={600} color="#e0e0e0">
                                                {entry.userName}
                                            </Typography>
                                            <Typography fontSize={10} color="#5a5a5a">{entry.userEmail}</Typography>
                                        </TableCell>
                                    )}

                                    <TableCell sx={tdStyle}><TypePill type={entry.type} /></TableCell>
                                    <TableCell sx={tdStyle}><DirectionPill direction={entry.direction} /></TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={14} fontWeight={700} color={directionMap[entry.direction]?.color || '#c0c0c0'}>
                                            {directionMap[entry.direction]?.sign}{entry.spinCount}
                                        </Typography>
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        {entry.referenceType ? (
                                            <Tooltip title={entry.referenceId || ''} arrow>
                                                <Box sx={{
                                                    display: 'inline-block', px: 1, py: 0.3, borderRadius: '5px',
                                                    fontSize: 10.5, fontWeight: 600, color: '#7a7a7a',
                                                    border: '1px solid #333', fontFamily: 'monospace',
                                                }}>
                                                    {entry.referenceType}
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Typography fontSize={11} color="#3a3a3a">-</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell sx={tdStyle}>
                                        <Typography fontSize={13} color="#e0e0e0">{formatDate(entry.createdAt)}</Typography>
                                        <Typography fontSize={10} color="#5a5a5a">{formatTime(entry.createdAt)}</Typography>
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={routeUserId ? 6 : 7} align="center" sx={{ py: 8, background: '#1a1a1a', border: 'none' }}>
                                        <CasinoIcon sx={{ color: '#2f2f2f', fontSize: 40, mb: 1 }} />
                                        <Typography color="#4a4a4a" fontSize={13}>No spin log entries found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    component="div"
                    count={meta.pagination.total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    showFirstButton showLastButton
                    sx={paginationStyle}
                />
            </Box>

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
        </div>
    )
}

export default SpinLogs

const thStyle = {
    backgroundColor: '#241815',
    color: '#ff6a3d',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.08em',
    textTransform: 'uppercase' as const,
    whiteSpace: 'nowrap' as const,
    py: 1.5,
    borderBottom: '1px solid #2a2a2a !important',
    position: 'sticky' as const,
    top: 0,
    zIndex: 2,
}

const tdStyle = {
    color: '#c0c0c0',
    py: 1.4,
    fontSize: 13,
    whiteSpace: 'nowrap' as const,
}

const trHover = {
    transition: 'background 0.15s',
    '&:hover': { background: '#ff6a3d08' },
}

const filterFieldStyle = {
    background: 'transparent',
    borderRadius: '7px',
    border: '1px solid #898989',
    transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff', fontSize: 13 },
    '& input::placeholder': { color: '#555', opacity: 1 },
}

const paginationStyle = {
    color: '#6a6a6a', borderTop: '1px solid #252525',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#6a6a6a', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#6a6a6a', margin: 0 },
    '& .MuiSelect-select': { color: '#6a6a6a' },
    '& .MuiSelect-icon': { color: '#6a6a6a' },
    '& .MuiIconButton-root': { color: '#6a6a6a' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#333' },
}